import { trackDesignSystemAction } from 'analytics';
import forge from 'apis/forge';
import { IconType } from 'components/Icon';
import ShareModal from 'components/Modal/ShareModal';
import Button, { ButtonState, ButtonType } from 'components/Widget/Button/Button';
import ExportDropdown from 'components/Widget/ExportDropdown';
import ExportMenuItem from 'components/Widget/ExportMenuItem';
import InlineRenamableTitle from 'components/Widget/InlineRenamableTitle';
import { Navbar, NavbarButton, NavbarCenter, NavbarLeft, NavbarRight } from 'components/Widget/Navbar';
import { DEFAULT_DS_TITLE } from 'dragonmouse-shared/constants';
import { interceptIfNoCredits } from 'economy';
import { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { selectActiveDesignSystem, selectActiveDesignSystemVersion } from 'redux/db/reducers';
import { setDsTitle } from 'redux/db/actions';
import { designSystemEditingBlocked } from 'redux/db/util';
import { showExportDsAiModal } from 'redux/mode/actions';
import { executeCommand } from 'redux/mode/commandManager';
import { UpdateDesignSystemCommand } from 'redux/mode/commands';
import { selectActiveDsTab } from 'redux/mode/reducers';
import {
	displayConfirmModal,
	displayDsReadOnlyModal,
	displayPublishDsModal,
} from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import store from 'redux/store';
import * as routes from 'routes';
import DsModeDropdown from './DsModeDropdown';
import Search from './Search';
import * as S from './styles';
import VersionDropdown from './VersionDropdown';

// Width thresholds based on the toolbar's width
const WIDTH_SEARCH_ICON = 800;
const WIDTH_SHARE_ICON = 700;
const WIDTH_SEARCH_HIDDEN = 600;
const WIDTH_DOWNLOAD_HIDDEN = 500;
const WIDTH_SHARE_HIDDEN = 420;
const WIDTH_PUBLISH_HIDDEN = 370;

const TopBar = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const {
		activeDesignSystem,
		activeDesignSystemId,
		activeDesignSystemVersion,
		isPreparing,
		isMigrating,
		historicalDsVersionNumber,
		isReadOnly,
		dsTitle,
		isDsVersionLoaded,
	} = useSelector(
		(r: RootReducer) => ({
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			activeDesignSystemId: r.mode.activeDesignSystemId,
			activeDesignSystemVersion: selectActiveDesignSystemVersion(r.db),
			isPreparing: r.db.dsPreparing,
			isMigrating: r.db.dsMigrating,
			historicalDsVersionNumber: r.mode.historicalDsVersionNumber,
			isReadOnly: r.db.loading.activeDesignSystemVersion === 'loaded' && !r.db.dsCanWrite,
			dsTitle: r.db.dsTitle,
			isDsVersionLoaded: r.db.loading.activeDesignSystemVersion === 'loaded',
		}),
		shallowEqual
	);
	const [showShareModal, setShowShareModal] = useState(false);

	// Auto-open share modal when ?share=open is in the URL
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		if (params.get('share') === 'open') {
			setShowShareModal(true);
			params.delete('share');
			const newUrl = params.toString()
				? `${window.location.pathname}?${params}`
				: window.location.pathname;
			window.history.replaceState({}, '', newUrl);
		}
	}, []);

	const navbarRef = useRef<HTMLDivElement | null>(null);
	const [toolbarWidth, setToolbarWidth] = useState(1200);

	useEffect(() => {
		const el = navbarRef.current;
		if (!el) return;
		setToolbarWidth(el.getBoundingClientRect().width);
		const ro = new ResizeObserver((entries) => {
			for (const entry of entries) {
				setToolbarWidth(entry.contentRect.width);
			}
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onDuplicate = useCallback(() => {
		if (!activeDesignSystemId) return;
		const sourceVersion = activeDesignSystemVersion?.versionNumber;
		if (sourceVersion == null) return;
		displayConfirmModal({
			title: 'Duplicate design system',
			description: 'This will create your own editable copy of this design system.',
			submitLabel: 'Duplicate',
			onSubmit: () => {
				const promise = (async () => {
					const res = await forge.duplicateDesignSystem({
						sourceDesignSystemId: activeDesignSystemId,
						sourceVersionNumber: sourceVersion,
					});
					trackDesignSystemAction('duplicate', { source: 'readonly_topbar' });
					navigate(routes.designSystem(res.designSystem.id, res.designSystem.title));
				})();
				toast.promise(promise, {
					pending: 'Duplicating design system...',
					success: 'Design system duplicated',
					error: 'Failed to duplicate design system',
				});
			},
		});
	}, [activeDesignSystemId, activeDesignSystemVersion?.versionNumber, navigate]);

	const onTitleCommit = useCallback(
		(trimmed: string) => {
			if (!activeDesignSystem) return;
			const originalTitle = dsTitle || DEFAULT_DS_TITLE;
			if (trimmed !== originalTitle) {
				trackDesignSystemAction('rename');
				// Optimistic update for the title bar (which reads r.db.dsTitle, not the optimistic DS write).
				dispatch(setDsTitle(trimmed));
				executeCommand(
					new UpdateDesignSystemCommand(
						{ id: activeDesignSystem.id, title: originalTitle },
						{ id: activeDesignSystem.id, title: trimmed }
					)
				);
			}
		},
		[activeDesignSystem, dsTitle, dispatch]
	);

	const onBeforeTitleStart = useCallback(() => {
		if (!activeDesignSystem || isReadOnly) return false;
		const blocked = designSystemEditingBlocked();
		if (blocked) {
			displayDsReadOnlyModal(blocked);
			return false;
		}
		return true;
	}, [activeDesignSystem, isReadOnly]);

	const onExportAi = () => {
		trackDesignSystemAction('open_export_ds_ai_modal');
		const tab = selectActiveDsTab(store.getState().mode);
		if (tab?.kind === 'components' && tab.contentPath) {
			// tab.contentPath is the prefixed tag name (e.g. "ds-button"); modal keys on bare dir name.
			const componentDir = tab.contentPath.replace(/^ds-/, '');
			dispatch(
				showExportDsAiModal({
					initialPreset: 'components',
					initialComponentNames: [componentDir],
				})
			);
		} else if (
			tab?.kind === 'colors' ||
			tab?.kind === 'typography' ||
			tab?.kind === 'styles'
		) {
			dispatch(showExportDsAiModal({ initialPreset: 'tokens' }));
		} else {
			dispatch(showExportDsAiModal({ initialPreset: 'all' }));
		}
	};

	const isDraft = activeDesignSystemVersion?.versionNumber === 0 && !isPreparing;
	const isViewingHistorical = historicalDsVersionNumber !== null;

	const searchMode =
		toolbarWidth < WIDTH_SEARCH_HIDDEN
			? 'hidden'
			: toolbarWidth < WIDTH_SEARCH_ICON
			? 'iconOnly'
			: 'full';
	const showShareText = toolbarWidth >= WIDTH_SHARE_ICON;
	const showDownload = toolbarWidth >= WIDTH_DOWNLOAD_HIDDEN;
	const showShare = toolbarWidth >= WIDTH_SHARE_HIDDEN;
	const showPublish = toolbarWidth >= WIDTH_PUBLISH_HIDDEN;

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const onPublish = () => {
		if (!activeDesignSystemId) return;
		if (isMigrating) return;
		const blocked = designSystemEditingBlocked();
		if (blocked) {
			displayDsReadOnlyModal(blocked);
			return;
		}
		if (interceptIfNoCredits({ kind: 'chatEdit', designSystemId: activeDesignSystemId })) {
			return;
		}
		displayPublishDsModal(activeDesignSystemId, dsTitle);
	};

	return (
		<>
			<Navbar layout="left-aligned" ref={navbarRef}>
				<NavbarLeft>
					<InlineRenamableTitle
						variant="navbar"
						value={isDsVersionLoaded ? dsTitle || DEFAULT_DS_TITLE : ''}
						editable={!isReadOnly && !!activeDesignSystem}
						onBeforeStart={onBeforeTitleStart}
						onCommit={onTitleCommit}
					/>
					<VersionDropdown />
					<DsModeDropdown />
				</NavbarLeft>

				{isReadOnly && (
					<NavbarCenter>
						<S.ReadOnlyBanner>
							Viewing · <S.ReadOnlyLink onClick={onDuplicate}>Duplicate to edit</S.ReadOnlyLink>
						</S.ReadOnlyBanner>
					</NavbarCenter>
				)}

				{!isPreparing && (
					<NavbarRight>
						<Search compactMode={searchMode} />
						{showDownload && (
							<S.Download>
								<ExportDropdown>
									<ExportMenuItem
										icon={IconType.GENERATE}
										label="Export for AI"
										description=".MD for Claude Code, ChatGPT, Codex…"
										onSelect={onExportAi}
									/>
								</ExportDropdown>
							</S.Download>
						)}
						{!isReadOnly && showPublish && (
							<S.PublishButton>
								<Button
									onSelected={onPublish}
									type={ButtonType.PUBLISH}
									state={
										isDraft && !isViewingHistorical ? ButtonState.REGULAR : ButtonState.DISABLED
									}
								>
									Publish
								</Button>
							</S.PublishButton>
						)}
						{showShare && (
							<S.Share>
								{showShareText ? (
									<Button
										onSelected={() => {
											trackDesignSystemAction('open_share_modal');
											setShowShareModal(true);
										}}
										type={ButtonType.NOTICEABLE}
									>
										Share
									</Button>
								) : (
									<NavbarButton
										icon={IconType.SHARE}
										onClick={() => {
											trackDesignSystemAction('open_share_modal');
											setShowShareModal(true);
										}}
										tooltip="Share"
										tooltipId="share-design-system-tooltip"
									/>
								)}
							</S.Share>
						)}
					</NavbarRight>
				)}
			</Navbar>
			{showShareModal && activeDesignSystem && (
				<ShareModal
					kind="designSystem"
					entityId={activeDesignSystem.id}
					onClose={() => setShowShareModal(false)}
				/>
			)}
		</>
	);
};

export default TopBar;
